/**
 * Customer ledger, allocation and ageing.
 *
 * The ledger is derived from documents rather than stored, so it can never
 * drift from them. Allocation decides which invoice a receipt settles, which is
 * what makes ageing meaningful — a customer who always pays the newest invoice
 * first looks current on a naive FIFO view while old debt quietly rots.
 */

import { computeDocumentTax, round2 } from "./tax";
import type { Receipt, ReceiptAllocation, SalesConfig, SalesDocument } from "./types";

export type LedgerEntryKind = "invoice" | "credit-note" | "debit-note" | "receipt" | "opening";

export interface LedgerEntry {
  id: string;
  date: string;
  kind: LedgerEntryKind;
  reference: string;
  narration: string;
  /** Increases what the customer owes. */
  debit: number;
  /** Reduces what the customer owes. */
  credit: number;
  runningBalance: number;
  documentId?: string;
  dueDate?: string;
}

export interface LedgerOptions {
  openingBalance?: number;
  openingDate?: string;
  from?: string;
  to?: string;
}

const inRange = (date: string, from?: string, to?: string): boolean => {
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
};

/**
 * Builds the running account for one partner. Debit-balance convention: an
 * invoice debits the customer, a receipt or credit note credits them, so a
 * positive closing balance is money owed to the business.
 */
export const buildLedger = (
  partnerId: string,
  documents: SalesDocument[],
  receipts: Receipt[],
  config: SalesConfig,
  options: LedgerOptions = {}
): { entries: LedgerEntry[]; closingBalance: number; totalDebit: number; totalCredit: number } => {
  const entries: LedgerEntry[] = [];

  if (options.openingBalance !== undefined && options.openingBalance !== 0) {
    entries.push({
      id: "opening",
      date: options.openingDate ?? options.from ?? "",
      kind: "opening",
      reference: "Opening balance",
      narration: "Balance brought forward",
      debit: options.openingBalance > 0 ? round2(options.openingBalance) : 0,
      credit: options.openingBalance < 0 ? round2(-options.openingBalance) : 0,
      runningBalance: 0
    });
  }

  // Only posted documents reach the ledger. A draft invoice is not a receivable.
  const posted = documents.filter(
    (document) =>
      document.partnerId === partnerId &&
      ["Posted", "Partially paid", "Paid"].includes(document.status) &&
      inRange(document.date, options.from, options.to)
  );

  for (const document of posted) {
    const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
      supplyType: document.supplyType,
      rcm: document.rcm,
      roundOff: config.roundOff,
      tcsRate: document.tcsRate
    });
    const isCredit = document.type === "credit-note";
    entries.push({
      id: document.id,
      date: document.date,
      kind: document.type === "debit-note" ? "debit-note" : isCredit ? "credit-note" : "invoice",
      reference: document.number,
      narration: isCredit
        ? `Credit note against ${document.againstInvoiceId ?? "invoice"}`
        : `${document.lines.length} line item${document.lines.length === 1 ? "" : "s"}`,
      debit: isCredit ? 0 : tax.total,
      credit: isCredit ? tax.total : 0,
      runningBalance: 0,
      documentId: document.id,
      dueDate: document.dueDate
    });
  }

  for (const receipt of receipts) {
    if (receipt.partnerId !== partnerId || receipt.status !== "Posted") continue;
    if (!inRange(receipt.date, options.from, options.to)) continue;
    entries.push({
      id: receipt.id,
      date: receipt.date,
      kind: "receipt",
      reference: receipt.number,
      narration: `Receipt by ${receipt.mode.replace("-", " ")}${receipt.reference ? ` · ${receipt.reference}` : ""}`,
      debit: 0,
      credit: round2(receipt.amount),
      runningBalance: 0,
      documentId: receipt.id
    });
    // TDS deducted by the customer settles part of the invoice without cash.
    if (receipt.tdsDeducted && receipt.tdsDeducted > 0) {
      entries.push({
        id: `${receipt.id}-tds`,
        date: receipt.date,
        kind: "receipt",
        reference: receipt.number,
        narration: `TDS deducted${receipt.tdsSection ? ` under section ${receipt.tdsSection}` : ""}`,
        debit: 0,
        credit: round2(receipt.tdsDeducted),
        runningBalance: 0,
        documentId: receipt.id
      });
    }
  }

  // Opening first, then by date, with debits before credits on the same day so
  // an invoice settled the day it is raised reads in the natural order.
  entries.sort((left, right) => {
    if (left.kind === "opening") return -1;
    if (right.kind === "opening") return 1;
    if (left.date !== right.date) return left.date < right.date ? -1 : 1;
    return right.debit - left.debit;
  });

  let balance = 0;
  let totalDebit = 0;
  let totalCredit = 0;
  for (const entry of entries) {
    balance = round2(balance + entry.debit - entry.credit);
    entry.runningBalance = balance;
    totalDebit = round2(totalDebit + entry.debit);
    totalCredit = round2(totalCredit + entry.credit);
  }

  return { entries, closingBalance: balance, totalDebit, totalCredit };
};

// ------------------------------------------------------------ Allocation

export interface OpenInvoice {
  id: string;
  number: string;
  date: string;
  dueDate?: string;
  total: number;
  allocated: number;
  outstanding: number;
}

/** Invoices with money still owing, oldest first. */
export const openInvoices = (
  partnerId: string,
  documents: SalesDocument[],
  receipts: Receipt[],
  config: SalesConfig
): OpenInvoice[] => {
  const allocatedByInvoice = new Map<string, number>();
  for (const receipt of receipts) {
    if (receipt.status !== "Posted") continue;
    // TDS the customer deducted settles the invoice alongside the cash. Without
    // this, every invoice paid net of TDS stays open by the deduction amount.
    const allocated = round2(receipt.allocations.reduce((sum, entry) => sum + entry.amount, 0));
    const tds = round2(receipt.tdsDeducted ?? 0);
    for (const allocation of receipt.allocations) {
      const tdsShare = allocated > 0 ? round2((tds * allocation.amount) / allocated) : 0;
      allocatedByInvoice.set(
        allocation.invoiceId,
        round2((allocatedByInvoice.get(allocation.invoiceId) ?? 0) + allocation.amount + tdsShare)
      );
    }
  }
  // A credit note against an invoice reduces what is collectable on it.
  for (const document of documents) {
    if (document.type !== "credit-note" || document.status !== "Posted" || !document.againstInvoiceId) continue;
    const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
      supplyType: document.supplyType,
      rcm: document.rcm,
      roundOff: config.roundOff
    });
    allocatedByInvoice.set(
      document.againstInvoiceId,
      round2((allocatedByInvoice.get(document.againstInvoiceId) ?? 0) + tax.total)
    );
  }

  return documents
    .filter(
      (document) =>
        document.partnerId === partnerId &&
        document.type === "invoice" &&
        ["Posted", "Partially paid"].includes(document.status)
    )
    .map((document) => {
      const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
        supplyType: document.supplyType,
        rcm: document.rcm,
        roundOff: config.roundOff,
        tcsRate: document.tcsRate
      });
      const allocated = round2(allocatedByInvoice.get(document.id) ?? 0);
      return {
        id: document.id,
        number: document.number,
        date: document.date,
        dueDate: document.dueDate,
        total: tax.total,
        allocated,
        outstanding: round2(tax.total - allocated)
      };
    })
    .filter((invoice) => invoice.outstanding > 0.009)
    .sort((left, right) => (left.date < right.date ? -1 : left.date > right.date ? 1 : 0));
};

export type AllocationStrategy = "fifo" | "oldest-due" | "manual";

/**
 * Spreads a receipt across open invoices. FIFO settles by invoice date;
 * oldest-due settles by due date, which matters when terms differ by invoice.
 * Anything left over stays unallocated as an advance.
 */
export const allocateReceipt = (
  amount: number,
  invoices: OpenInvoice[],
  strategy: AllocationStrategy = "fifo"
): { allocations: ReceiptAllocation[]; unallocated: number } => {
  if (strategy === "manual") return { allocations: [], unallocated: round2(amount) };

  const ordered = [...invoices].sort((left, right) => {
    if (strategy === "oldest-due") {
      const leftDue = left.dueDate ?? left.date;
      const rightDue = right.dueDate ?? right.date;
      if (leftDue !== rightDue) return leftDue < rightDue ? -1 : 1;
    }
    return left.date < right.date ? -1 : left.date > right.date ? 1 : 0;
  });

  let remaining = round2(amount);
  const allocations: ReceiptAllocation[] = [];
  for (const invoice of ordered) {
    if (remaining <= 0.009) break;
    const applied = round2(Math.min(remaining, invoice.outstanding));
    if (applied <= 0) continue;
    allocations.push({ invoiceId: invoice.id, invoiceNumber: invoice.number, amount: applied });
    remaining = round2(remaining - applied);
  }
  return { allocations, unallocated: round2(remaining) };
};

/** Validates a manual allocation before it is saved. */
export const validateAllocation = (
  receiptAmount: number,
  allocations: ReceiptAllocation[],
  invoices: OpenInvoice[]
): string[] => {
  const errors: string[] = [];
  const total = round2(allocations.reduce((sum, allocation) => sum + allocation.amount, 0));
  if (total > round2(receiptAmount) + 0.009) {
    errors.push(`Allocated ${total.toFixed(2)} exceeds the receipt amount of ${round2(receiptAmount).toFixed(2)}.`);
  }
  const byId = new Map(invoices.map((invoice) => [invoice.id, invoice]));
  const seen = new Set<string>();
  for (const allocation of allocations) {
    if (allocation.amount <= 0) errors.push(`Allocation to ${allocation.invoiceNumber} must be greater than zero.`);
    if (seen.has(allocation.invoiceId)) errors.push(`${allocation.invoiceNumber} appears more than once.`);
    seen.add(allocation.invoiceId);
    const invoice = byId.get(allocation.invoiceId);
    if (!invoice) {
      errors.push(`${allocation.invoiceNumber} is not an open invoice for this partner.`);
    } else if (allocation.amount > invoice.outstanding + 0.009) {
      errors.push(`Allocation of ${allocation.amount.toFixed(2)} to ${allocation.invoiceNumber} exceeds its outstanding ${invoice.outstanding.toFixed(2)}.`);
    }
  }
  return errors;
};

// --------------------------------------------------------------- Ageing

/** Operational buckets used day to day for collections. */
export const OPERATIONAL_BUCKETS = [
  { label: "Not due", from: -Infinity, to: 0 },
  { label: "0-30 days", from: 1, to: 30 },
  { label: "31-60 days", from: 31, to: 60 },
  { label: "61-90 days", from: 61, to: 90 },
  { label: "91-180 days", from: 91, to: 180 },
  { label: "Over 180 days", from: 181, to: Infinity }
];

/** Schedule III disclosure buckets, measured from the transaction date. */
export const SCHEDULE_III_BUCKETS = [
  { label: "Less than 6 months", from: 0, to: 180 },
  { label: "6 months - 1 year", from: 181, to: 365 },
  { label: "1-2 years", from: 366, to: 730 },
  { label: "2-3 years", from: 731, to: 1095 },
  { label: "More than 3 years", from: 1096, to: Infinity }
];

export interface AgeingRow {
  partnerId: string;
  partnerName: string;
  total: number;
  buckets: Record<string, number>;
  oldestInvoiceDate?: string;
  oldestDays: number;
}

export interface AgeingOptions {
  asAt: string;
  /** Age from the due date (collections view) or the invoice date (Schedule III). */
  basis?: "due-date" | "invoice-date";
  buckets?: Array<{ label: string; from: number; to: number }>;
  defaultCreditDays?: number;
}

const daysBetween = (from: string, to: string): number =>
  Math.floor((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000);

export const buildAgeing = (
  partners: Array<{ id: string; name: string }>,
  documents: SalesDocument[],
  receipts: Receipt[],
  config: SalesConfig,
  options: AgeingOptions
): { rows: AgeingRow[]; totals: Record<string, number>; grandTotal: number; bucketLabels: string[] } => {
  const buckets = options.buckets ?? (options.basis === "invoice-date" ? SCHEDULE_III_BUCKETS : OPERATIONAL_BUCKETS);
  const bucketLabels = buckets.map((bucket) => bucket.label);
  const basis = options.basis ?? "due-date";

  const rows: AgeingRow[] = [];
  const totals: Record<string, number> = Object.fromEntries(bucketLabels.map((label) => [label, 0]));

  for (const partner of partners) {
    const open = openInvoices(partner.id, documents, receipts, config);
    if (!open.length) continue;

    const partnerBuckets: Record<string, number> = Object.fromEntries(bucketLabels.map((label) => [label, 0]));
    let total = 0;
    let oldestDays = 0;
    let oldestInvoiceDate: string | undefined;

    for (const invoice of open) {
      const reference =
        basis === "due-date"
          ? invoice.dueDate ??
            new Date(new Date(invoice.date).getTime() + (options.defaultCreditDays ?? config.defaultCreditDays) * 86_400_000)
              .toISOString()
              .slice(0, 10)
          : invoice.date;
      const age = daysBetween(reference, options.asAt);
      const bucket = buckets.find((candidate) => age >= candidate.from && age <= candidate.to) ?? buckets[buckets.length - 1];
      partnerBuckets[bucket.label] = round2(partnerBuckets[bucket.label] + invoice.outstanding);
      totals[bucket.label] = round2(totals[bucket.label] + invoice.outstanding);
      total = round2(total + invoice.outstanding);
      const ageFromInvoice = daysBetween(invoice.date, options.asAt);
      if (ageFromInvoice > oldestDays) {
        oldestDays = ageFromInvoice;
        oldestInvoiceDate = invoice.date;
      }
    }

    rows.push({ partnerId: partner.id, partnerName: partner.name, total, buckets: partnerBuckets, oldestInvoiceDate, oldestDays });
  }

  rows.sort((left, right) => right.total - left.total);
  const grandTotal = round2(rows.reduce((sum, row) => sum + row.total, 0));
  return { rows, totals, grandTotal, bucketLabels };
};

/**
 * Days sales outstanding on the count-back method, which is more honest than
 * the simple ratio when sales are lumpy: work backwards through recent months
 * consuming the closing receivable against actual billing.
 */
export const daysSalesOutstanding = (
  closingReceivable: number,
  monthlySales: Array<{ month: string; sales: number; days: number }>
): number => {
  let remaining = round2(closingReceivable);
  let days = 0;
  for (let index = monthlySales.length - 1; index >= 0 && remaining > 0; index -= 1) {
    const period = monthlySales[index];
    if (period.sales <= 0) continue;
    if (remaining >= period.sales) {
      days += period.days;
      remaining = round2(remaining - period.sales);
    } else {
      days += Math.round((remaining / period.sales) * period.days);
      remaining = 0;
    }
  }
  return days;
};

/** Statement of account for sending to a customer. */
export interface StatementSummary {
  partnerId: string;
  partnerName: string;
  from: string;
  to: string;
  openingBalance: number;
  totalDebit: number;
  totalCredit: number;
  closingBalance: number;
  entries: LedgerEntry[];
  openInvoices: OpenInvoice[];
  overdueAmount: number;
}

export const buildStatement = (
  partner: { id: string; name: string },
  documents: SalesDocument[],
  receipts: Receipt[],
  config: SalesConfig,
  options: { from: string; to: string; openingBalance?: number }
): StatementSummary => {
  const ledger = buildLedger(partner.id, documents, receipts, config, {
    from: options.from,
    to: options.to,
    openingBalance: options.openingBalance,
    openingDate: options.from
  });
  const open = openInvoices(partner.id, documents, receipts, config);
  const overdueAmount = round2(
    open
      .filter((invoice) => {
        const due =
          invoice.dueDate ??
          new Date(new Date(invoice.date).getTime() + config.defaultCreditDays * 86_400_000).toISOString().slice(0, 10);
        return due < options.to;
      })
      .reduce((sum, invoice) => sum + invoice.outstanding, 0)
  );

  return {
    partnerId: partner.id,
    partnerName: partner.name,
    from: options.from,
    to: options.to,
    openingBalance: round2(options.openingBalance ?? 0),
    totalDebit: ledger.totalDebit,
    totalCredit: ledger.totalCredit,
    closingBalance: ledger.closingBalance,
    entries: ledger.entries,
    openInvoices: open,
    overdueAmount
  };
};

